/* Casanvero — motion layer for the product sections.
   [data-mo]          adds .mo-in the first time the element is properly on screen
   [data-mo-scroll]   gets --mp (0 → 1) as it travels through the viewport
   [data-mo-split]    headings split into words for a staggered reveal
   [data-mo-steps]    "how it works": lights each step in turn and fills the progress spine
   [data-mo-spot]     a soft spotlight that follows the pointer
   canvas[data-gl]    a WebGL light shader ("liquid" or "rays") that only runs while visible */
(() => {
  const doc = document;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  /* ---------- split headings into words (text nodes only, <br> kept) ---------- */
  doc.querySelectorAll('[data-mo-split]').forEach((el) => {
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((c) => {
        if (c.nodeType === 3) {
          const frag = doc.createDocumentFragment();
          c.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(part)); return; }
            const w = doc.createElement('span'); w.className = 'mo-w';
            const inner = doc.createElement('span'); inner.textContent = part; inner.style.setProperty('--i', i++);
            w.appendChild(inner); frag.appendChild(w);
          });
          c.replaceWith(frag);
        } else if (c.nodeType === 1 && c.tagName !== 'BR') walk(c);
      });
    };
    walk(el);
    el.classList.add('mo-split');
  });

  /* ---------- in view ---------- */
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    if (e.isIntersecting) { e.target.classList.add('mo-in'); io.unobserve(e.target); }
  }), { threshold: 0.22, rootMargin: '0px 0px -8% 0px' });
  doc.querySelectorAll('[data-mo]').forEach((el) => (reduce ? el.classList.add('mo-in') : io.observe(el)));

  /* ---------- scroll progress ---------- */
  const scrollers = [...doc.querySelectorAll('[data-mo-scroll]')].map((el) => ({ el, steps: el.hasAttribute('data-mo-steps') ? [...el.querySelectorAll('[data-mo-step]')] : null, last: -1 }));
  const hud = (s, i) => {
    const t = s.el.querySelector('[data-mo-hud-title]'); const n = s.el.querySelector('[data-mo-hud-num]');
    const step = s.steps[i];
    if (t && step) t.textContent = step.getAttribute('data-mo-step');
    if (n) n.textContent = String(i + 1).padStart(2, '0');
  };
  const update = () => {
    const vh = window.innerHeight;
    scrollers.forEach((s) => {
      const r = s.el.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      const p = clamp((vh - r.top) / (vh + r.height), 0, 1);
      s.el.style.setProperty('--mp', p.toFixed(4));
      if (s.steps && s.steps.length) {
        // steps follow how far the list itself has come up the screen
        const list = s.el.querySelector('[data-mo-list]') || s.el;
        const lr = list.getBoundingClientRect();
        const lp = clamp((vh * 0.72 - lr.top) / Math.max(1, lr.height), 0, 1);
        s.el.style.setProperty('--sp', lp.toFixed(4));
        const i = Math.min(s.steps.length - 1, Math.floor(lp * s.steps.length * 0.999));
        if (i !== s.last) {
          s.steps.forEach((st, k) => { st.classList.toggle('is-on', k <= i); st.classList.toggle('is-now', k === i); });
          hud(s, i); s.last = i;
        }
      }
    });
  };
  let ticking = false;
  window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; update(); }); } }, { passive: true });
  window.addEventListener('resize', update);
  update();

  /* ---------- pointer spotlight ---------- */
  doc.querySelectorAll('[data-mo-spot]').forEach((el) => el.addEventListener('pointermove', (e) => {
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
    el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
  }));

  /* ---------- WebGL light ---------- */
  const COMMON = `precision highp float;
uniform vec2 r; uniform float t; uniform float p;
float h(vec2 q){ return fract(sin(dot(q, vec2(127.1, 311.7))) * 43758.5453); }
float n(vec2 q){ vec2 i = floor(q), f = fract(q); f = f * f * (3. - 2. * f);
  return mix(mix(h(i), h(i + vec2(1, 0)), f.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), f.x), f.y); }
float fbm(vec2 q){ float v = 0., a = .5; for (int i = 0; i < 5; i++) { v += a * n(q); q = q * 2.02 + 3.1; a *= .5; } return v; }
`;
  const SHADERS = {
    // molten silk in the brand palette: domain-warped noise, lit from the right
    liquid: COMMON + `void main(){
  vec2 uv = gl_FragCoord.xy / r; vec2 q = uv * vec2(r.x / r.y, 1.) * 1.35;
  float T = t * .045;
  vec2 a = vec2(fbm(q + vec2(0., T)), fbm(q + vec2(5.2, 1.3) - T));
  vec2 b = vec2(fbm(q + 3.2 * a + vec2(1.7, 9.2) + T * 1.4), fbm(q + 3.2 * a + vec2(8.3, 2.8) - T));
  float f = fbm(q + 3.4 * b);
  vec3 ink = vec3(.07, .06, .052), cocoa = vec3(.21, .13, .09), ember = vec3(.78, .39, .24), light = vec3(1., .74, .56);
  vec3 c = mix(ink, cocoa, smoothstep(.25, .75, f));
  c = mix(c, ember, smoothstep(.62, 1.05, f * (.7 + length(b))) * .85);
  c += light * pow(smoothstep(.55, 1., f * length(a) * 1.25), 4.) * .55;
  c *= .55 + .6 * smoothstep(1.25, .1, length((uv - vec2(.78, .45)) * vec2(1.4, 1.)));
  c += (h(gl_FragCoord.xy + fract(t)) - .5) * .03;
  gl_FragColor = vec4(c, 1.);
}`,
    // light shafts and drifting out-of-focus dust, composited with "screen"
    rays: COMMON + `void main(){
  vec2 uv = gl_FragCoord.xy / r; float ar = r.x / r.y; vec2 a = vec2(uv.x * ar, uv.y);
  vec2 src = vec2(.82 * ar, 1.25 + p * .15);
  vec2 d = a - src; float ang = atan(d.y, d.x);
  float rays = pow(max(0., sin(ang * 13. + t * .12)), 9.) * .55 + pow(max(0., sin(ang * 21. - t * .09 + 1.3)), 12.) * .4 + pow(max(0., sin(ang * 7. + t * .05 + 2.)), 6.) * .3;
  float fall = smoothstep(1.9, .1, length(d)) * (.6 + .4 * n(vec2(ang * 3., t * .1)));
  vec3 col = vec3(1., .74, .52) * rays * fall * .42;
  float dust = 0.;
  for (int i = 0; i < 34; i++) {
    float fi = float(i);
    vec2 pos = vec2(fract(h(vec2(fi, 1.)) + t * .004 * (.4 + h(vec2(fi, 2.)))), fract(h(vec2(fi, 3.)) + t * .007 * (.3 + h(vec2(fi, 4.)))));
    pos.x *= ar;
    float depth = h(vec2(fi, 5.));
    float s = .003 + .028 * depth * depth;
    float dd = length(a - pos);
    float disc = smoothstep(s, s * (.55 - depth * .3), dd);
    dust += disc * (.18 + .55 * (1. - depth)) * (.55 + .45 * sin(t * (.4 + depth) + fi * 1.7));
  }
  col += vec3(1., .83, .64) * dust * (.18 + .5 * fall);
  col += (h(gl_FragCoord.xy + fract(t * 1.3)) - .5) * .035;
  gl_FragColor = vec4(max(col, 0.), 1.);
}`,
  };
  const VERT = 'attribute vec2 v; void main(){ gl_Position = vec4(v, 0., 1.); }';
  const glItems = [];
  doc.querySelectorAll('canvas[data-gl]').forEach((cv) => {
    const src = SHADERS[cv.getAttribute('data-gl')]; if (!src) return;
    const gl = cv.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
    if (!gl) { cv.remove(); return; }
    const sh = (type, s) => { const o = gl.createShader(type); gl.shaderSource(o, s); gl.compileShader(o); return o; };
    const prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, src)); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { cv.remove(); return; }
    gl.useProgram(prog);
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'v'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const item = { cv, gl, uR: gl.getUniformLocation(prog, 'r'), uT: gl.getUniformLocation(prog, 't'), uP: gl.getUniformLocation(prog, 'p'), live: false, scale: +(cv.getAttribute('data-gl-scale') || 0.6), seed: Math.random() * 100 };
    glItems.push(item);
    new IntersectionObserver((es) => { item.live = es[0].isIntersecting; if (item.live) loop(); }, { rootMargin: '80px' }).observe(cv);
  });
  const size = (it) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5) * it.scale;
    const w = Math.max(2, Math.round(it.cv.clientWidth * dpr)), h = Math.max(2, Math.round(it.cv.clientHeight * dpr));
    if (it.cv.width !== w || it.cv.height !== h) { it.cv.width = w; it.cv.height = h; it.gl.viewport(0, 0, w, h); }
  };
  const draw = (it, now) => {
    size(it);
    const host = it.cv.closest('[data-mo-scroll]');
    const mp = host ? +getComputedStyle(host).getPropertyValue('--mp') || 0 : 0;
    it.gl.uniform2f(it.uR, it.cv.width, it.cv.height);
    it.gl.uniform1f(it.uT, it.seed + (reduce ? 0 : now / 1000));
    if (it.uP) it.gl.uniform1f(it.uP, mp);
    it.gl.drawArrays(it.gl.TRIANGLES, 0, 3);
  };
  let running = false;
  function loop() {
    if (running) return; running = true;
    const frame = (now) => {
      const live = glItems.filter((it) => it.live);
      live.forEach((it) => draw(it, now));
      if (live.length && !reduce) requestAnimationFrame(frame); else running = false;
    };
    requestAnimationFrame(frame);
  }
})();
